import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const NAV = [
  { label: "Services", href: "#services", key: "nav.services" },
  { label: "About", href: "#about", key: "nav.about" },
  { label: "Meaning", href: "#brand", key: "nav.brand" },
  { label: "How it Works", href: "#how-it-works", key: "nav.how" },
  { label: "Testimonials", href: "#testimonials", key: "nav.testimonials" },
  { label: "FAQ", href: "#faq", key: "nav.faq" },
];

function LanguageToggle() {
  const { lang, setLang } = useT();
  return (
    <button
      data-testid="language-toggle"
      onClick={() => setLang(lang === "en" ? "ht" : "en")}
      aria-label="Switch language / Chanje lang"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#BCB3A6]/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#3F5043] hover:border-[#3F5043] hover:bg-[#EDEDEE]/60 transition-colors"
    >
      <Globe size={13} />
      {lang === "en" ? "Kreyòl" : "English"}
    </button>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F8F7F3]/80 border-b border-[#BCB3A6]/30"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <button
            data-testid="site-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_client-attract-7/artifacts/ejvjbazd_IMG_0045.jpeg"
              alt="Soarlife Consulting"
              className="h-11 w-11 rounded-full object-cover"
            />
            <div className="flex flex-col items-start leading-tight">
              <span className="font-serif italic text-2xl text-[#3F5043] tracking-tight">
                Soarlife
              </span>
              <span className="text-[0.6rem] uppercase tracking-[0.25em] text-[#899480] font-semibold -mt-0.5">
                {t("header.tagline")}
              </span>
            </div>
          </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => goto(n.href)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-[#3F5043]/80 hover:text-[#3F5043] transition-colors relative group"
            >
              {t(n.key)}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#899480] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link
            to="/portal"
            data-testid="nav-client-portal"
            className="text-sm font-semibold text-[#875F45] hover:text-[#3F5043] transition-colors relative group"
          >
            {t("nav.portal")}
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#875F45] transition-all duration-300 group-hover:w-full" />
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link
            data-testid="header-book-btn"
            to="/book"
            className="hidden sm:inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium bg-[#3F5043] text-[#F8F7F3] hover:bg-[#899480] transition-colors duration-300"
          >
            {t("header.book")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-[#3F5043]"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#BCB3A6]/30 bg-[#F8F7F3]/95 backdrop-blur-xl"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => goto(n.href)}
                className="text-left text-base text-[#3F5043] py-2 border-b border-[#BCB3A6]/20"
              >
                {t(n.key)}
              </button>
            ))}
            <Link
              to="/portal"
              data-testid="mobile-nav-client-portal"
              onClick={() => setOpen(false)}
              className="text-left text-base font-semibold text-[#875F45] py-2 border-b border-[#BCB3A6]/20"
            >
              {t("nav.portal")}
            </Link>
            <div className="pt-1">
              <LanguageToggle />
            </div>
            <Link
              data-testid="mobile-header-book-btn"
              to="/book"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium text-center"
            >
              {t("header.book")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
